import { getApiClient } from '@/lib/api/api-client';
import type { Installation } from '@/types';
import { logger } from '@/lib/utils/logger';
import { getCacheDuration } from '@/lib/config/cache.config';

const DEBUG = process.env.NODE_ENV === 'development';
import type {
  BaseQueryParams,
  PaginatedResponse,
  CatalogItem,
  FindInstallationsParams,
  WhereFilter,
} from './catalog-types';
import { getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { installationTransformer } from '@/lib/transformers/installations.transformer';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';

export function parseSearchParams(params: URLSearchParams): Partial<BaseQueryParams> {
  return {
    page: params.get('page') ? parseInt(params.get('page')!) : undefined,
    limit: params.get('limit') ? parseInt(params.get('limit')!) : 10,
    query: params.get('query') || undefined,
    depth: params.get('depth') ? parseInt(params.get('depth')!) : undefined,
    sort: params.get('sort') || undefined,
  };
}

/**
 * Transform Installation to CatalogItem
 */
export function installationToCatalogItem(installation: Installation): CatalogItem {
  return installationTransformer.toCatalogItem(installation);
}

/**
 * API service for Installations
 */
export class InstallationsAPI {
  static apiClient = getApiClient();
  static CACHE_DURATION = getCacheDuration('installations'); // From centralized config

  /**
   * Get installation list
   */
  static async findInstallations(
    params: FindInstallationsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    try {
      const { page = 1, limit = 10, query, sort, years, ...rest } = params;

      const sortValue = getSortValue(sort, { dateField: 'createdAt' });

      // Build where query for Payload CMS v3
      const where: WhereFilter = {};

      // Search query
      if (query) {
        where.title = {
          contains: query,
        };
      }

      // Year filter - use year field directly for Installation
      if (years && years.length > 0) {
        try {
          if (years.length === 1) {
            // Single year selected, use equals
            where.year = {
              equals: Number(years[0]),
            };
          } else {
            // Multiple years, use in operator
            where.year = {
              in: years.map(year => Number(year)),
            };
          }
        } catch (error) {
          // Log error but continue with other filters
        }
      }

      // Additional where parameters
      if (rest.where) {
        Object.assign(where, rest.where);
      }

      if (DEBUG) {
        logger.warn('Installations API where query:', where);
      }

      const response = await InstallationsAPI.apiClient.collections.installations.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        // TODO: type properly - getSortValue returns dynamic strings not in payload-rest-client's keyof DOC union
        sort: sortValue as never,
      });

      // Cast API response to Installation type
      const result = response as PaginatedResponse<Installation>;

      // Calculate totalPages and check pagination
      if (result.totalDocs && result.limit) {
        const totalPages = Math.ceil(result.totalDocs / result.limit);
        result.totalPages = totalPages;

        // Check if there are more pages
        result.hasNextPage = result.page < totalPages;

        // Transform results to CatalogItem
        const catalogItems = result.docs.map(installationToCatalogItem);

        if (DEBUG) {
          logger.warn(
            `API response calculated: Page ${result.page}/${totalPages}, hasNextPage: ${result.hasNextPage}, totalDocs: ${result.totalDocs}, limit: ${result.limit}`
          );
        }

        // Return result as new object to avoid type mismatch
        return success({
          ...result,
          docs: catalogItems,
        } as PaginatedResponse<CatalogItem>);
      } else {
        // Default to false if totalDocs or limit is missing
        result.hasNextPage = false;
        if (DEBUG) {
          logger.warn(`API response calculated: hasNextPage: false (missing totalDocs or limit)`);
        }

        // Return empty docs array as CatalogItem array
        return success({
          ...result,
          docs: [],
        } as PaginatedResponse<CatalogItem>);
      }
    } catch (error) {
      return handleApiError('findInstallations', error);
    }
  }

  /**
   * Get installation details by slug
   */
  static async findInstallationBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Installation>> {
    try {
      const cacheKey = await CacheService.scopedKey(`installation.service_${slug}`);
      // Check cache (if skipCache is false)
      if (!skipCache) {
        const cachedData = CacheService.getFromCache(cacheKey);
        if (cachedData) {
          return success(cachedData as Installation);
        }
      }

      const response = await InstallationsAPI.apiClient.collections.installations.find({
        where: {
          slug: {
            equals: slug,
          },
        },
        depth: 2,
        limit: 1,
      });

      if (response.docs.length === 0) {
        // Not found is not an error, just return null
        return success(null as unknown as Installation);
      }

      // Write to cache
      CacheService.addToCache(cacheKey, response.docs[0], InstallationsAPI.CACHE_DURATION);
      return success(response.docs[0]);
    } catch (error) {
      return handleApiError('findInstallationBySlug', error);
    }
  }
}
